import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  QueryReturnType,
  mutatePatchFn,
} from "../../services/networking.service";
import ENDPOINTS from "../../services/endPoints";

export interface KeyHostRequestFilters {
  page?: number;
  limit?: number;
  /** pending | fulfilled | cancelled | expired — omit for all */
  status?: string;
}

/**
 * GET /admin/key-host-requests — the "request a key host near me" queue
 * (key_host_requests collection; written by the iOS app via
 * POST /owner/requestKeyHost). Each row: the owner, the address + lat/long,
 * number_of_keys, notes and a status. This is NOT the "Host Slot Requests"
 * screen (that one is owners waitlisted at a specific full host).
 *
 * The axios response interceptor camelCases the body, so fields are read
 * camelCase: number_of_keys -> numberOfKeys, date_needed -> dateNeeded,
 * location_type -> locationType, fulfilled_at -> fulfilledAt.
 */
export const useKeyHostRequests = (
  filters: KeyHostRequestFilters = {}
): QueryReturnType<any> => {
  return useQuery<never, never, any>(
    [
      ENDPOINTS.KEY_HOST_REQUESTS,
      { ...filters, status: filters.status || undefined },
    ],
    {
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      staleTime: 30 * 1000,
    }
  );
};

/**
 * GET /admin/key-host-requests/analytics — status counts + the areas with the
 * most pending requests (so an admin can see "4 owners want a host near SW1").
 */
export const useKeyHostRequestAnalytics = (): QueryReturnType<any> => {
  return useQuery<never, never, any>([ENDPOINTS.KEY_HOST_REQUEST_ANALYTICS], {
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 60 * 1000,
  });
};

/** PATCH /admin/key-host-requests/:requestId — set status fulfilled|cancelled|expired. */
export const useUpdateKeyHostRequest = () => {
  const qc = useQueryClient();
  return useMutation(
    ({ id, status }: { id: string; status: string }) =>
      mutatePatchFn(
        ENDPOINTS.KEY_HOST_REQUEST_UPDATE.replace(":requestId", id),
        { status }
      ),
    {
      onSuccess: () => {
        qc.invalidateQueries([ENDPOINTS.KEY_HOST_REQUESTS]);
        qc.invalidateQueries([ENDPOINTS.KEY_HOST_REQUEST_ANALYTICS]);
      },
    }
  );
};
